import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "./ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { Input } from "./ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Pause, Ban, Play, Scissors, Merge, Flame } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function ReasonDialog({ open, onClose, title, icon: Icon, colorClass, actionLabel, onSubmit, requireReason = true, extra = null }) {
  const [reason, setReason] = useState("");
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (requireReason && (!reason || reason.trim().length < 3)) {
      toast.error("Indique um motivo (mín. 3 caracteres)");
      return;
    }
    setBusy(true);
    try {
      await onSubmit({ reason: reason.trim(), notes: notes.trim() });
      setReason(""); setNotes("");
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-lg" data-testid="order-action-dialog">
        <DialogHeader>
          <DialogTitle className={`flex items-center gap-2 ${colorClass}`}>
            <Icon className="h-5 w-5" /> {title}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3 py-2">
          {requireReason && (
            <>
              <div>
                <Label>Motivo *</Label>
                <Input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Ex: Falta de matéria-prima" data-testid="reason-input" />
              </div>
              <div>
                <Label>Notas (opcional)</Label>
                <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={3} data-testid="notes-input" />
              </div>
            </>
          )}
          {extra}
        </div>
        <div className="flex justify-end gap-2 pt-2">
          <Button variant="outline" onClick={onClose}>Cancelar</Button>
          <Button onClick={submit} disabled={busy} data-testid="action-submit-btn">{busy ? "..." : actionLabel}</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default function OrderActions({ order, onDone }) {
  const [action, setAction] = useState(null); // 'suspend' | 'cancel' | 'split' | 'priority'
  const [splitQty, setSplitQty] = useState(1);
  const [splitReason, setSplitReason] = useState("");
  const [prioReason, setPrioReason] = useState("");
  const [newPriority, setNewPriority] = useState(order?.priority || "normal");

  if (!order) return null;
  const status = order.status;

  const resume = async () => {
    try {
      await axios.post(`${API}/orders/${order.id}/resume`);
      toast.success("Ordem retomada");
      onDone?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro");
    }
  };

  const canSuspend = ["pending", "running", "paused"].includes(status);
  const canCancel = ["pending", "running", "paused", "suspended"].includes(status);
  const canResume = status === "suspended";
  const canSplit = ["pending", "running", "paused"].includes(status);

  return (
    <>
      <div className="flex flex-wrap gap-2">
        {canSuspend && (
          <Button size="sm" variant="outline" className="text-amber-600 border-amber-300 hover:bg-amber-50" onClick={() => setAction("suspend")} data-testid="btn-suspend">
            <Pause className="h-4 w-4 mr-1" /> Suspender
          </Button>
        )}
        {canResume && (
          <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 text-white" onClick={resume} data-testid="btn-resume">
            <Play className="h-4 w-4 mr-1" /> Retomar
          </Button>
        )}
        {canCancel && (
          <Button size="sm" variant="outline" className="text-red-600 border-red-300 hover:bg-red-50" onClick={() => setAction("cancel")} data-testid="btn-cancel">
            <Ban className="h-4 w-4 mr-1" /> Cancelar
          </Button>
        )}
        {canSplit && (
          <Button size="sm" variant="outline" className="text-blue-600 border-blue-300 hover:bg-blue-50" onClick={() => setAction("split")} data-testid="btn-split">
            <Scissors className="h-4 w-4 mr-1" /> Dividir
          </Button>
        )}
        <Button size="sm" variant="outline" className="text-purple-600 border-purple-300 hover:bg-purple-50" onClick={() => setAction("priority")} data-testid="btn-priority">
          <Flame className="h-4 w-4 mr-1" /> Prioridade
        </Button>
      </div>

      {action === "suspend" && (
        <ReasonDialog
          open onClose={() => setAction(null)}
          title={`Suspender ${order.code}`} icon={Pause} colorClass="text-amber-700"
          actionLabel="Suspender"
          onSubmit={async ({ reason, notes }) => {
            await axios.post(`${API}/orders/${order.id}/suspend`, { reason, notes });
            toast.success("Ordem suspensa");
            onDone?.();
          }}
        />
      )}

      {action === "cancel" && (
        <ReasonDialog
          open onClose={() => setAction(null)}
          title={`Cancelar ${order.code}`} icon={Ban} colorClass="text-red-700"
          actionLabel="Cancelar Ordem"
          onSubmit={async ({ reason, notes }) => {
            await axios.post(`${API}/orders/${order.id}/cancel`, { reason, notes });
            toast.success("Ordem cancelada");
            onDone?.();
          }}
        />
      )}

      {action === "split" && (
        <ReasonDialog
          open onClose={() => setAction(null)}
          title={`Dividir ${order.code}`} icon={Scissors} colorClass="text-blue-700"
          actionLabel="Dividir"
          requireReason={false}
          extra={
            <>
              <div>
                <Label>Quantidade para a NOVA ordem *</Label>
                <Input type="number" min={1} max={order.good_quantity_target - 1} value={splitQty} onChange={(e) => setSplitQty(parseInt(e.target.value) || 1)} data-testid="split-qty" />
                <p className="text-xs text-gray-500 mt-1">Ordem original: {order.good_quantity_target} peças. Nova ordem terá {splitQty}, original ficará com {order.good_quantity_target - splitQty}.</p>
              </div>
              <div>
                <Label>Motivo (opcional)</Label>
                <Input placeholder="Ex: Urgência do cliente" value={splitReason} onChange={(e) => setSplitReason(e.target.value)} />
              </div>
            </>
          }
          onSubmit={async () => {
            if (splitQty < 1 || splitQty >= order.good_quantity_target) {
              throw new Error("Quantidade inválida");
            }
            const r = await axios.post(`${API}/orders/${order.id}/split`, { split_quantity: splitQty, reason: splitReason || null });
            toast.success(r.data.message);
            onDone?.();
          }}
        />
      )}

      {action === "priority" && (
        <ReasonDialog
          open onClose={() => setAction(null)}
          title={`Prioridade — ${order.code}`} icon={Flame} colorClass="text-purple-700"
          actionLabel="Guardar"
          requireReason={false}
          extra={
            <>
              <div>
                <Label>Nova prioridade</Label>
                <Select value={newPriority} onValueChange={setNewPriority}>
                  <SelectTrigger data-testid="priority-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="urgent">🔥 URGENTE</SelectItem>
                    <SelectItem value="high">⚡ Alta</SelectItem>
                    <SelectItem value="normal">Normal</SelectItem>
                    <SelectItem value="low">Baixa</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Motivo (opcional)</Label>
                <Input placeholder="Ex: Cliente prioritário" value={prioReason} onChange={(e) => setPrioReason(e.target.value)} />
              </div>
            </>
          }
          onSubmit={async () => {
            await axios.put(`${API}/orders/${order.id}/priority`, { priority: newPriority, reason: prioReason || null });
            toast.success("Prioridade actualizada");
            onDone?.();
          }}
        />
      )}
    </>
  );
}
